// Which real game statuses count as "active/live" for the web client.
// The provider (BALLDONTLIE) marks active games as `in_progress`; `halftime`
// and `delayed` are also active (game underway, scores meaningful). Treat all of
// these like `live` so real active games are never hidden or shown as upcoming.
export const LIVE_STATUSES = ["live", "in_progress", "halftime", "delayed"];

export function isLiveStatus(status) {
  return LIVE_STATUSES.includes(status);
}

export function isFinalStatus(status) {
  return status === "final";
}

// Combine the canonical /home buckets into one bounded ticker feed
// (live + close + upcoming + recent finals), deduped by game id, order kept.
// This gives the ticker fresh live, genuine future, and recent final games in a
// single authenticated request. orderTickerGames still classifies/filters it.
export function combineHomeGames(home) {
  const seen = new Set();
  const out = [];
  for (const bucket of [home && home.live, home && home.close_games, home && home.upcoming, home && home.recently_final]) {
    for (const g of (Array.isArray(bucket) ? bucket : [])) {
      if (g && g.id && !seen.has(g.id)) { seen.add(g.id); out.push(g); }
    }
  }
  return out;
}

// Statuses that represent a genuine, not-yet-started game. Only these can ever
// be shown as "upcoming" — and only when their kickoff is still in the future.
export const SCHEDULED_STATUSES = ["scheduled", "upcoming", "pre", "pregame"];

// Anything that is not live, not final, and not a genuine future scheduled game
// is treated as unavailable: stale/provider-unavailable statuses (e.g. postponed,
// canceled, suspended, tbd, unknown/empty) AND scheduled records whose kickoff
// has already passed (or is missing). These must never be shown as live/upcoming.
// Returns one of: "live" | "final" | "upcoming" | "unavailable".
export function classifyGame(game, now = Date.now()) {
  const status = game && game.status;
  if (isLiveStatus(status)) return "live";
  if (isFinalStatus(status)) return "final";
  if (SCHEDULED_STATUSES.includes(status)) {
    const t = Date.parse(game && game.start_time);
    return Number.isFinite(t) && t > now ? "upcoming" : "unavailable";
  }
  return "unavailable";
}

// Score-ticker ordering: active LIVE first, then newest RECENT FINALS, then
// genuine UPCOMING (kickoff ascending). Live sorted by kickoff asc, finals newest
// first (start_time desc), upcoming by kickoff asc. Stale/provider-unavailable
// and expired-scheduled records are filtered out entirely — the ticker only
// surfaces real live/final/upcoming games. Pure + shared with the ScoreTicker.
const TICKER_RANK = { live: 0, final: 1, upcoming: 2 };
export function orderTickerGames(games, now = Date.now()) {
  return [...(Array.isArray(games) ? games : [])]
    .map((g) => ({ g, cls: classifyGame(g, now) }))
    .filter((x) => x.cls !== "unavailable")
    .sort((a, b) => {
      const r = TICKER_RANK[a.cls] - TICKER_RANK[b.cls];
      if (r !== 0) return r;
      const ta = Date.parse(a.g.start_time) || 0;
      const tb = Date.parse(b.g.start_time) || 0;
      return a.cls === "final" ? tb - ta : ta - tb; // finals newest-first; others asc
    })
    .map((x) => x.g);
}

// ── Shared polling policy ───────────────────────────────────────────────────
// Fast while a game is live OR a non-final game is inside its active kickoff
// window (30 min before → 6 h after kickoff, even if still "scheduled"). Slow
// otherwise so late provider reconciliation still appears.
export const FAST_POLL_MS = 45000;
export const SLOW_POLL_MS = 300000;
export const KICKOFF_PRE_MS = 30 * 60 * 1000;
export const KICKOFF_POST_MS = 6 * 60 * 60 * 1000;

export function isGameActive(game, now = Date.now()) {
  if (!game) return false;
  if (isLiveStatus(game.status)) return true;
  if (isFinalStatus(game.status)) return false;
  const t = Date.parse(game.start_time);
  if (!Number.isFinite(t)) return false;
  return now >= t - KICKOFF_PRE_MS && now <= t + KICKOFF_POST_MS;
}

// Ticker cadence: fast if any game is active, else slow (never stops).
export function pollIntervalForGames(games, now = Date.now()) {
  const list = Array.isArray(games) ? games : [];
  return list.some((g) => isGameActive(g, now)) ? FAST_POLL_MS : SLOW_POLL_MS;
}

// Single-game cadence (GameDetail): fast while active, slow while a non-final
// game is otherwise open, and STOP (null) once the game is final.
export function pollIntervalForGame(game, now = Date.now()) {
  if (!game || isFinalStatus(game.status)) return null;
  return isGameActive(game, now) ? FAST_POLL_MS : SLOW_POLL_MS;
}
